import * as SwitchPrimitive from '@radix-ui/react-switch';
import * as React from 'react';

type SwitchCommonProps = {
  size?: 'md' | 'lg';
};
type SwitchRootProps = React.ComponentProps<typeof SwitchPrimitive.Root> &
  SwitchCommonProps;
type SwitchThumbProps = React.ComponentProps<typeof SwitchPrimitive.Thumb> &
  SwitchCommonProps;

import { cn } from '../utils';
const SwitchRoot = React.forwardRef<
  React.ElementRef<typeof SwitchPrimitive.Root>,
  SwitchRootProps
>(({ className, size, ...props }, ref) => (
  <SwitchPrimitive.Root
    data-slot="switch"
    className={cn(
      'peer border-2 border-input data-[state=checked]:border-primary data-[state=checked]:bg-primary data-[state=unchecked]:bg-input focus-visible:border-ring focus-visible:ring-ring/50 dark:data-[state=unchecked]:bg-input/80 relative inline-flex h-5 w-9 shrink-0 items-center rounded-full shadow-xs transition-all outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50',
      size === 'lg' && 'h-6 w-10',
      className,
    )}
    ref={ref}
    {...props}
  >
    {props.children}
  </SwitchPrimitive.Root>
));

const SwitchThumb = React.forwardRef<
  React.ElementRef<typeof SwitchPrimitive.Thumb>,
  SwitchThumbProps
>(({ className, size, ...props }, ref) => (
  <SwitchPrimitive.Thumb
    ref={ref}
    data-slot="switch-thumb"
    className={cn(
      'rounded-full pointer-events-none block ring-0 bg-background',
      'transition-all duration-200 ease-in-out',
      'h-full aspect-square translate-x-0 data-[state=checked]:translate-x-full',
      size === 'lg' && 'data-[state=checked]:translate-x-[calc(100%-4px)]',
      className,
    )}
    {...props}
  />
));

const Switch = React.forwardRef<
  React.ElementRef<typeof SwitchPrimitive.Root>,
  SwitchRootProps
>(({ className, size, ...props }, ref) => (
  <SwitchRoot className={className} size={size} {...props} ref={ref}>
    <SwitchThumb size={size} />
  </SwitchRoot>
));

export { Switch, SwitchRoot, SwitchThumb };
